import { Card, FlexContent, Table, TableCell, TableHeadCell, TableRow } from "@rothoblaas/design-system";
import { memo, useCallback, useMemo, type FC, type RefObject } from "react";
import { useFormContext } from "react-hook-form";
import useI18n from "../../../hooks/useTranslations";
import type { DeckDesignerResult } from "../../../http/deckDesigner/formulas";
import { L_SHAPE } from "../../../http/deckDesigner/types/configurations";
import type { DeckDesignerConfigurationFormValues } from "../../../http/deckDesigner/useAutoSaveDeckDesignerConfiguration";
import BottomMeasurementsLayer from "./BottomMeasurementsLayer";
import BottomStructureLayer from "./BottomStructureLayer";
import CartesianPlane, { type PlaneBounds } from "./CartesianPlane";
import DeckBoardsLayer from "./DeckBoardsLayer";
import styles from "./DeckBottomViewCard.module.css";
import { useMeasurementFormat } from "./measurementFormat";
import {
  computeSupportReactions,
  getBottomViewMeasurementExamples,
  getSupportNodeReferencePoints,
  getSupportSpacingInfo,
} from "./deckBottomViewGeometry";
import { getCoordinateBounds } from "./deckGeometry";
import DeckShapeLayer from "./DeckShapeLayer";
import { useDeckBottomViewGeometry } from "./useDeckBottomViewGeometry";

type DeckBottomViewCardProps = {
  className?: string;
  embedded?: boolean;
  result: DeckDesignerResult;
  planeContentRef?: RefObject<HTMLDivElement | null>;
  // When true, hides the interactive grid/axis chrome and fits the whole deck in view (for a clean export snapshot).
  exportMode?: boolean;
  // Leaves out the joist/support dimension lines (and their labels) - for the support-reaction PDF
  // page, whose drawing only marks node types.
  hideMeasurements?: boolean;
  // Colours the one reference support per node type (see getSupportNodeReferencePoints) - the ones
  // the support-reaction PDF page numbers.
  highlightSupportNodeReferences?: boolean;
};

const DeckBottomViewCard: FC<DeckBottomViewCardProps> = ({
  className,
  embedded = false,
  result,
  planeContentRef,
  exportMode = false,
  hideMeasurements = false,
  highlightSupportNodeReferences = false,
}) => {
  const i18n = useI18n();
  const measurementFormat = useMeasurementFormat();
  const { control } = useFormContext<DeckDesignerConfigurationFormValues>();

  const {
    boardSegments,
    bottomLayout,
    coordinates,
    isStripeSupport,
    joistWidthMeters,
    selectedShape,
    supportFamilyCode,
  } = useDeckBottomViewGeometry(result, control);

  const spacingFormatter = useMemo(
    () =>
      new Intl.NumberFormat(i18n.translation.languageCode, {
        maximumFractionDigits: 3,
      }),
    [i18n.translation.languageCode],
  );
  const highlightedSupports = useMemo(
    () =>
      highlightSupportNodeReferences && typeof result.liveLoad === "number"
        ? [
            ...getSupportNodeReferencePoints(
              computeSupportReactions(
                bottomLayout.joists,
                result.supportQuantity,
                supportFamilyCode,
                isStripeSupport,
                result.liveLoad,
              ),
            ).values(),
          ]
        : [],
    [
      bottomLayout.joists,
      highlightSupportNodeReferences,
      isStripeSupport,
      result.liveLoad,
      result.supportQuantity,
      supportFamilyCode,
    ],
  );
  const supportSpacingInfo = useMemo(
    () => getSupportSpacingInfo(bottomLayout.joists, result.supportQuantity, supportFamilyCode, isStripeSupport),
    [bottomLayout.joists, result.supportQuantity, supportFamilyCode, isStripeSupport],
  );
  const measurementExamples = useMemo(
    () =>
      getBottomViewMeasurementExamples(bottomLayout.joists, result.supportQuantity, supportFamilyCode, isStripeSupport),
    [bottomLayout.joists, result.supportQuantity, supportFamilyCode, isStripeSupport],
  );
  const shapeBounds = useMemo(() => getCoordinateBounds(coordinates), [coordinates]);
  const unitM = i18n.translation.common.unitM;
  const unitPcs = i18n.translation.common.unitPcs;
  // Stable while the language/unit are: BottomMeasurementsLayer builds its labels from them.
  const formatSupportSpacing = useCallback(
    (spacingMeters: number | null): string =>
      spacingMeters == null
        ? "-"
        : measurementFormat.length(spacingMeters, "m", `${spacingFormatter.format(spacingMeters)} ${unitM}`),
    [measurementFormat, spacingFormatter, unitM],
  );
  const formatSupportCount = useCallback(
    (supportCount: number | null | undefined): string =>
      typeof supportCount === "number" && Number.isFinite(supportCount)
        ? `${spacingFormatter.format(supportCount)} ${unitPcs}`
        : "-",
    [spacingFormatter, unitPcs],
  );
  const shouldShowSupportSpacing = supportSpacingInfo.long != null || supportSpacingInfo.short != null;
  // No export-specific margin needed - the labels themselves no longer render into the raster (the
  // PDF draws them separately, see getBottomViewMeasurementLabels), so there's nothing left that can
  // overshoot the shape's own bounds and risk being clipped by the plane's overflow:hidden.
  const fitBounds: PlaneBounds | null = exportMode ? shapeBounds : null;

  const content = (
    <FlexContent className={styles.content}>
      <CartesianPlane contentRef={planeContentRef} exportMode={exportMode} fitBounds={fitBounds}>
        {({ toScreenPoint }) => (
          <>
            <DeckShapeLayer
              coordinates={coordinates}
              maxCoordinates={coordinates.length}
              outlineClassName={styles.deckOutline}
              shapeClassName={styles.deckShape}
              toScreenPoint={toScreenPoint}
            />
            <DeckBoardsLayer boardSegments={boardSegments} toScreenPoint={toScreenPoint} />
            <BottomStructureLayer
              isStripeSupport={isStripeSupport}
              joists={bottomLayout.joists}
              joistWidthMeters={joistWidthMeters}
              supports={bottomLayout.supports}
              highlightedSupports={highlightedSupports}
              toScreenPoint={toScreenPoint}
            />
            {!hideMeasurements && (
              <BottomMeasurementsLayer
                bounds={shapeBounds}
                examples={measurementExamples}
                exportMode={exportMode}
                formatSpacing={formatSupportSpacing}
                formatSupportCount={formatSupportCount}
                supportsCountLabel={i18n.translation.deckDesignerModule.configurations.calculation.supportsCount}
                toScreenPoint={toScreenPoint}
              />
            )}
          </>
        )}
      </CartesianPlane>
      <div className={styles.legendBlock}>
        <FlexContent direction="row" className={styles.legend}>
          <FlexContent direction="row" className={styles.legendItem}>
            <svg width="14" height="14" viewBox="0 0 14 14" aria-hidden="true">
              {isStripeSupport ? (
                <line x1="1" y1="7" x2="13" y2="7" stroke="#f0bc42" strokeWidth="3" strokeLinecap="round" />
              ) : (
                <circle cx="7" cy="7" r="5" fill="#f0bc42" stroke="#6f4a00" strokeWidth="1.4" />
              )}
            </svg>
            <span>{": " + i18n.translation.common.supports}</span>
          </FlexContent>
          <FlexContent direction="row" className={styles.legendItem}>
            <svg width="26" height="12" viewBox="0 0 26 12" aria-hidden="true">
              <line x1="3" y1="6" x2="23" y2="6" stroke="#1f2328" strokeLinecap="round" strokeWidth="5" />
            </svg>
            <span>{": " + i18n.translation.deckDesignerModule.configurations.calculation.joistsLegend}</span>
          </FlexContent>
        </FlexContent>
        {shouldShowSupportSpacing && (
          <Table dense wrapperClassName={styles.supportInfo}>
            <thead>
              <TableRow>
                <TableHeadCell align="left">{i18n.translation.common.type}</TableHeadCell>
                <TableHeadCell align="right">
                  {i18n.translation.deckDesignerModule.configurations.calculation.supportSpacing}
                </TableHeadCell>
                <TableHeadCell align="right">
                  {i18n.translation.deckDesignerModule.configurations.calculation.supportsCount}
                </TableHeadCell>
              </TableRow>
            </thead>
            <tbody>
              <TableRow>
                <TableCell>{i18n.translation.deckDesignerModule.configurations.calculation.longJoist}</TableCell>
                <TableCell className={styles.numericCell}>{formatSupportSpacing(supportSpacingInfo.long)}</TableCell>
                <TableCell className={styles.numericCell}>{formatSupportCount(result.supportQuantity?.long)}</TableCell>
              </TableRow>
              {selectedShape === L_SHAPE && (
                <TableRow>
                  <TableCell>{i18n.translation.deckDesignerModule.configurations.calculation.shortJoist}</TableCell>
                  <TableCell className={styles.numericCell}>{formatSupportSpacing(supportSpacingInfo.short)}</TableCell>
                  <TableCell className={styles.numericCell}>
                    {formatSupportCount(result.supportQuantity?.short)}
                  </TableCell>
                </TableRow>
              )}
            </tbody>
          </Table>
        )}
      </div>
    </FlexContent>
  );

  return embedded ? (
    content
  ) : (
    <Card
      style={{ minHeight: 400 }}
      className={className}
      accentColor="var(--accent-base, var(--red))"
      shadow
      title={i18n.translation.common.titlePreview}
    >
      {content}
    </Card>
  );
};

// Memoized: its props are stable, so it re-renders through its own watched fields and props only, not
// whenever its parent does.
export default memo(DeckBottomViewCard);
